/**
 * useShopCart — panier local du tunnel /boutique/[slug].
 *
 * Centralise :
 *  - lignes produit × variante × quantité (+ prix libre + champs perso ligne)
 *  - persistance sessionStorage PAR BOUTIQUE (`shopcart:{slug}`)
 *  - résolution variante par défaut (produit sans axes)
 *  - prix unitaire appliqué (override variante / prix libre / gratuit)
 *  - plafonds : stock restant par variante + `max_per_order` par produit
 *  - clamp réactif si le stock change (pattern useEventTicketSelection)
 *
 * ⚠️ MONTANTS EN EUROS (convention ticket_types.price).
 */
import type { Ref } from "vue";
import type {
  Shop,
  ShopProduct,
  ShopProductVariant,
  ShopCartLine,
  ShopCartLineView,
} from "~/types/shop";

const CART_VERSION = 1;

function makeUid(): string {
  return `l_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Stock restant effectif d'une variante (`null` = illimité). */
export function getVariantRemaining(variant: ShopProductVariant | null): number | null {
  if (!variant) return null;
  // Préférer le champ calculé serveur (`remaining`) — source de vérité
  // partagée avec l'anti-oversell (pattern billetterie).
  if (variant.remaining !== undefined && variant.remaining !== null) {
    return Math.max(0, variant.remaining);
  }
  if (variant.remaining === null) return null;
  if (variant.stock === null || variant.stock === undefined) return null;
  return Math.max(0, variant.stock - (variant.sold_count || 0));
}

/** Une variante est épuisée si le serveur le dit ou si le restant tombe à 0. */
export function isVariantSoldOut(variant: ShopProductVariant | null): boolean {
  if (!variant) return false;
  if (variant.sold_out === true) return true;
  const remaining = getVariantRemaining(variant);
  return remaining !== null && remaining <= 0;
}

/** Libellé lisible d'une combinaison, ex. « Taille M · Noir ». */
export function formatCombinationLabel(
  combination: Record<string, string> | null | undefined,
): string {
  if (!combination) return "";
  return Object.values(combination).join(" · ");
}

export function useShopCart(slug: Ref<string>, shop: Ref<Shop | null>) {
  const lines = ref<ShopCartLine[]>([]);

  // ─── Produits / variantes ────────────────────────────────────────
  const products = computed<ShopProduct[]>(() =>
    (shop.value?.products || [])
      .filter((p) => p.is_active)
      .slice()
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)),
  );

  function getProduct(productId: string): ShopProduct | null {
    return products.value.find((p) => p.id === productId) || null;
  }

  /**
   * Résout la variante d'une ligne. Produit sans axes ⇒ variante par défaut
   * implicite (la seule variante active, `combination` null).
   */
  function resolveVariant(
    product: ShopProduct | null,
    variantId?: string,
  ): ShopProductVariant | null {
    if (!product) return null;
    const variants = (product.variants || []).filter((v) => v.is_active);
    if (variantId) return variants.find((v) => v.id === variantId) || null;
    if (variants.length === 1) return variants[0] ?? null;
    return variants.find((v) => !v.combination) || null;
  }

  /** Prix unitaire appliqué en EUROS pour un produit + variante (+ prix libre). */
  function getUnitPrice(
    product: ShopProduct,
    variant: ShopProductVariant | null,
    customPrice?: number,
  ): number {
    if (product.pricing === "free") return 0;
    if (product.pricing === "flexible") return Math.max(0, Number(customPrice || 0));
    const override = variant?.price_override;
    return Number(override ?? product.price ?? 0);
  }

  // ─── Lignes enrichies / totaux ───────────────────────────────────
  const cartLines = computed<ShopCartLineView[]>(() => {
    const views: ShopCartLineView[] = [];
    for (const line of lines.value) {
      const product = getProduct(line.product_id);
      if (!product) continue;
      const variant = resolveVariant(product, line.variant_id);
      const unitPrice = getUnitPrice(product, variant, line.custom_price);
      views.push({
        ...line,
        product,
        variant,
        variantLabel: formatCombinationLabel(variant?.combination),
        unitPrice,
        lineTotal: Math.round(unitPrice * line.quantity * 100) / 100,
      });
    }
    return views;
  });

  const totalItems = computed(() =>
    cartLines.value.reduce((sum, l) => sum + l.quantity, 0),
  );

  const subtotal = computed(() =>
    Math.round(cartLines.value.reduce((sum, l) => sum + l.lineTotal, 0) * 100) / 100,
  );

  // ─── Plafonds ────────────────────────────────────────────────────
  /** Quantité déjà au panier pour un produit (toutes lignes), hors ligne exclue. */
  function quantityInCartForProduct(productId: string, excludeUid?: string): number {
    return lines.value
      .filter((l) => l.product_id === productId && l.uid !== excludeUid)
      .reduce((sum, l) => sum + l.quantity, 0);
  }

  /** Quantité déjà au panier pour une variante précise, hors ligne exclue. */
  function quantityInCartForVariant(
    productId: string,
    variantId: string | undefined,
    excludeUid?: string,
  ): number {
    return lines.value
      .filter(
        (l) =>
          l.product_id === productId &&
          (l.variant_id || "") === (variantId || "") &&
          l.uid !== excludeUid,
      )
      .reduce((sum, l) => sum + l.quantity, 0);
  }

  /**
   * Quantité max ajoutable/settable pour une ligne = min(stock restant de la
   * variante − autres lignes de la même variante, max_per_order du produit −
   * autres lignes du même produit). `Infinity` si illimité.
   */
  function getMaxForLine(
    product: ShopProduct,
    variant: ShopProductVariant | null,
    excludeUid?: string,
  ): number {
    let max = Infinity;
    const remaining = getVariantRemaining(variant);
    if (remaining !== null) {
      max = Math.min(
        max,
        Math.max(0, remaining - quantityInCartForVariant(product.id, variant?.id, excludeUid)),
      );
    }
    if (product.max_per_order) {
      max = Math.min(
        max,
        Math.max(0, product.max_per_order - quantityInCartForProduct(product.id, excludeUid)),
      );
    }
    return max;
  }

  // ─── Mutations ───────────────────────────────────────────────────
  function addLine(input: {
    product: ShopProduct;
    variant: ShopProductVariant | null;
    quantity: number;
    custom_price?: number;
    custom_data?: Record<string, unknown>;
  }): boolean {
    const { product, variant } = input;
    const max = getMaxForLine(product, variant);
    const qty = Math.min(Math.max(1, Math.floor(input.quantity)), max);
    if (qty <= 0 || !Number.isFinite(qty)) return false;

    const hasCustomData = !!input.custom_data && Object.keys(input.custom_data).length > 0;
    // Fusion avec une ligne identique (même variante, même prix libre, sans
    // champs perso — une personnalisation reste une ligne distincte).
    const existing = lines.value.find(
      (l) =>
        l.product_id === product.id &&
        (l.variant_id || "") === (variant?.id || "") &&
        !hasCustomData &&
        (!l.custom_data || Object.keys(l.custom_data).length === 0) &&
        (l.custom_price ?? 0) === (input.custom_price ?? 0),
    );
    if (existing) {
      existing.quantity += qty;
    } else {
      lines.value.push({
        uid: makeUid(),
        product_id: product.id,
        variant_id: variant?.id,
        quantity: qty,
        custom_price: product.pricing === "flexible" ? input.custom_price : undefined,
        custom_data: hasCustomData ? input.custom_data : undefined,
      });
    }
    return true;
  }

  function setLineQuantity(uid: string, quantity: number) {
    const line = lines.value.find((l) => l.uid === uid);
    if (!line) return;
    const product = getProduct(line.product_id);
    if (!product) return;
    const variant = resolveVariant(product, line.variant_id);
    const max = getMaxForLine(product, variant, uid);
    const qty = Math.min(Math.max(0, Math.floor(quantity)), max);
    if (qty <= 0) {
      removeLine(uid);
    } else {
      line.quantity = qty;
    }
  }

  function removeLine(uid: string) {
    lines.value = lines.value.filter((l) => l.uid !== uid);
  }

  function clearCart() {
    lines.value = [];
  }

  // ─── Clamp (stock qui bouge / restauration de session) ───────────
  /**
   * Écarte les lignes dont le produit/la variante a disparu et ramène chaque
   * quantité au plafond courant. Appelé par le watch produits ET après la
   * restauration sessionStorage (les produits ne changent pas à ce moment-là).
   */
  function clampLines() {
    if (lines.value.length === 0) return;
    // 1. Écarter les lignes dont le produit/la variante a disparu.
    lines.value = lines.value.filter((l) => {
      const product = getProduct(l.product_id);
      if (!product) return false;
      const variant = resolveVariant(product, l.variant_id);
      if (l.variant_id && !variant) return false;
      return true;
    });
    // 2. Clamp des quantités au plafond courant.
    for (const line of lines.value) {
      const product = getProduct(line.product_id);
      if (!product) continue;
      const variant = resolveVariant(product, line.variant_id);
      const max = getMaxForLine(product, variant, line.uid);
      if (Number.isFinite(max) && line.quantity > max) {
        line.quantity = Math.max(0, max);
      }
    }
    lines.value = lines.value.filter((l) => l.quantity > 0);
  }

  // La SOURCE du watch dérive des produits (pas de lecture de `lines` dans la
  // source) → écrire dans `lines` ici ne re-déclenche pas le watch en boucle.
  watch(
    () =>
      products.value.map((p) => ({
        id: p.id,
        maxPerOrder: p.max_per_order ?? null,
        variants: (p.variants || []).map((v) => ({
          id: v.id,
          remaining: getVariantRemaining(v),
          active: v.is_active,
        })),
      })),
    () => clampLines(),
    { deep: true },
  );

  // ─── Persistance sessionStorage par boutique ─────────────────────
  const storageKey = computed(() => `shopcart:${slug.value}`);

  function persist() {
    if (!import.meta.client) return;
    try {
      sessionStorage.setItem(
        storageKey.value,
        JSON.stringify({ v: CART_VERSION, lines: lines.value }),
      );
    } catch {
      // intentional empty: quota sessionStorage non-fatal
    }
  }

  function restore() {
    if (!import.meta.client) return;
    try {
      const raw = sessionStorage.getItem(storageKey.value);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { v?: number; lines?: ShopCartLine[] };
      if (parsed.v !== CART_VERSION || !Array.isArray(parsed.lines)) return;
      lines.value = parsed.lines.filter(
        (l) => l && typeof l.product_id === "string" && (l.quantity || 0) > 0,
      );
      // Le stock a pu bouger depuis la sauvegarde (le watch produits ne se
      // déclenche pas ici) → clamp immédiat contre l'état courant.
      clampLines();
    } catch {
      // intentional empty: session corrompue → panier vide
    }
  }

  if (import.meta.client) {
    onMounted(() => restore());
    watch(lines, () => persist(), { deep: true });
  }

  return {
    lines,
    cartLines,
    products,
    totalItems,
    subtotal,
    getProduct,
    resolveVariant,
    getUnitPrice,
    getMaxForLine,
    addLine,
    setLineQuantity,
    removeLine,
    clearCart,
  };
}
